import { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/Avatar";
import { Panel, Quiet, ScrollList } from "@/components/home/panel";
import { currencySymbol, fmtGel } from "@/lib/money";
import { presenceLabel, relativeAgo } from "@/lib/presence";
import { roleLabel, type BrokerBenchmarks } from "@/types";
import type {
  ExceptionReason,
  ExceptionRow,
  MoneyBlock as Money,
  PersonRef,
  PipelineHealth,
  PresenceSummary,
} from "@/pages/home/types";

/** Blocks shared by the manager / operations / apex homes.
 *
 *  All of these are built to sit inside a fixed grid cell without pushing the
 *  page past one screen: lists scroll inside their panel rather than growing
 *  it, and every row is one line tall wherever the content allows. */

// ── exceptions ───────────────────────────────────────────────────────────────

/** All exception copy lives here. The API ships `{code, count, detail}` and the
 *  client writes the sentence, so wording never forks across Python and TS. */
function phrase(r: ExceptionReason): string {
  switch (r.code) {
    case "dormant":
      return "Nothing published this week";
    case "stopped":
      return "Nothing published today";
    case "jobs_failing":
      return `${r.count} publish runs failed`;
    case "deals_stalled":
      return `${r.count} deal${r.count === 1 ? "" : "s"} going cold`;
    case "no_credentials":
      return "No portal credentials";
    default:
      return r.code;
  }
}

const DOT: Record<string, string> = {
  critical: "bg-rose-400",
  warning: "bg-amber-400",
  info: "bg-slate-500",
};

const TEXT: Record<string, string> = {
  critical: "text-rose-300",
  warning: "text-amber-300",
  info: "text-slate-400",
};

export function ExceptionPanel({
  rows,
  title = "Needs attention",
  emptyHint = "Nobody needs chasing right now.",
  className = "",
}: {
  rows: ExceptionRow[];
  title?: string;
  emptyHint?: string;
  className?: string;
}) {
  return (
    <Panel
      title={title}
      right={rows.length ? `${rows.length}` : undefined}
      className={className}
      flush
    >
      {rows.length === 0 ? (
        <Quiet title="All clear" hint={emptyHint} />
      ) : (
        // 270 rather than the default 320: this is the tallest panel in the
        // left column, and the extra rows only ever arrive by scrolling
        // anyway — the header carries the true count.
        <ScrollList max={255} deps={rows.length}>
          {rows.map((r) => (
            <Link
              key={r.person.id}
              to={`/team/${r.person.id}`}
              className="flex items-center gap-2.5 px-4 py-2 hover:bg-white/[0.02] transition min-w-0"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${DOT[r.severity]}`}
              />
              <Avatar name={r.person.full_name || r.person.email} size="xs" />
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] text-slate-200 truncate">
                  {r.person.full_name || r.person.email}
                </span>
                <span className="block text-[11px] truncate">
                  <span className={TEXT[r.severity]}>{phrase(r.reasons[0])}</span>
                  {r.reasons[0].detail && (
                    <span className="text-slate-500"> · {r.reasons[0].detail}</span>
                  )}
                  {r.reasons.length > 1 && (
                    <span className="text-slate-500">
                      {" "}
                      +{r.reasons.length - 1} more
                    </span>
                  )}
                </span>
              </span>
              <span className="text-[10.5px] text-slate-600 flex-shrink-0 tabular-nums">
                {r.person.presence === "active"
                  ? "online"
                  : relativeAgo(r.person.last_active_at) ?? ""}
              </span>
            </Link>
          ))}
        </ScrollList>
      )}
    </Panel>
  );
}

// ── roster ───────────────────────────────────────────────────────────────────

export function RosterPanel({
  rows,
  goalDaily,
}: {
  rows: BrokerBenchmarks[];
  goalDaily: number;
}) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  const onTarget = rows.filter(
    (r) => goalDaily > 0 && r.placements_today >= goalDaily,
  ).length;

  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-white/[0.02] transition"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Everyone · {rows.length}
        </span>
        <span className="text-[11px] text-slate-500 tabular-nums">
          {onTarget} on target · {open ? "hide" : "show"}
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-white/5">
          <table className="w-full text-[12.5px]">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="text-left font-medium px-4 py-1.5">Person</th>
                <th className="text-right font-medium px-3 py-1.5">Today</th>
                <th className="text-right font-medium px-3 py-1.5">Month</th>
                <th className="text-right font-medium px-3 py-1.5">On target</th>
                <th className="text-right font-medium px-3 py-1.5">Deals</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-b border-white/[0.04] last:border-0">
                  <td className="px-4 py-1.5">
                    <Link
                      to={`/team/${r.user_id}`}
                      className="flex items-center gap-2 min-w-0 hover:underline decoration-white/20"
                    >
                      <Avatar name={r.full_name || r.email || "?"} size="xs" />
                      <span className="text-slate-200 truncate max-w-[150px]">
                        {r.full_name || r.email}
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    <span
                      className={
                        goalDaily > 0 && r.placements_today >= goalDaily
                          ? "text-emerald-300"
                          : "text-slate-300"
                      }
                    >
                      {r.placements_today || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">
                    {r.placements_this_month.toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                    {r.placement_days_hit}/{r.placement_days_elapsed}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    <span className="text-emerald-300">{r.deals_done}</span>
                    <span className="text-slate-600">/</span>
                    <span className="text-slate-500">{r.deals_lost}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── pipeline / money / people ───────────────────────────────────────────────

export function PipelinePanel({ p }: { p: PipelineHealth }) {
  const total = p.done + p.error + p.cancelled;
  const pct = Math.round(p.error_rate * 100);
  const tone = pct >= 20 ? "text-rose-300" : pct >= 10 ? "text-amber-300" : "text-emerald-300";
  return (
    <Panel
      title="Publishing · 7 days"
      right={<span className={tone}>{pct}% failing</span>}
    >
      <div className="flex h-1.5 rounded-full overflow-hidden bg-white/[0.06]">
        {total > 0 && (
          <>
            <div className="bg-emerald-500" style={{ width: `${(p.done / total) * 100}%` }} />
            <div className="bg-rose-500" style={{ width: `${(p.error / total) * 100}%` }} />
            <div className="bg-amber-500" style={{ width: `${(p.cancelled / total) * 100}%` }} />
          </>
        )}
      </div>
      <div className="mt-2 flex gap-3 text-[10.5px] text-slate-500 tabular-nums">
        <span><span className="text-emerald-300">{p.done.toLocaleString()}</span> done</span>
        <span><span className="text-rose-300">{p.error}</span> failed</span>
        <span><span className="text-amber-300">{p.cancelled}</span> cancelled</span>
      </div>
    </Panel>
  );
}

/** Currencies sit side by side rather than summed: converting would need an
 *  as-of FX rate this block does not carry, and a wrong total beats no total
 *  only in the eyes of whoever isn't relying on it. */
export function MoneyPanel({ money }: { money: Money }) {
  const net = money.commission_gel - money.expenses_gel;
  // Reported in GEL, matching the Finance page. The per-currency split stays
  // as supporting detail — useful context, but not two totals to add up.
  const split = Object.entries(money.commission_by_currency)
    .sort((a, b) => b[1] - a[1])
    .map(
      ([cur, amt]) =>
        `${currencySymbol(cur) || `${cur} `}${amt.toLocaleString(undefined, {
          maximumFractionDigits: 0,
        })}`,
    )
    .join(" · ");

  return (
    <Panel title="Commission this month" right={`${money.deals_done} closed`}>
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-2xl font-semibold tabular-nums leading-none text-emerald-300">
          {fmtGel(String(Math.round(money.commission_gel)))}
        </span>
        {split && (
          <span className="text-[11px] text-slate-500 truncate">{split}</span>
        )}
      </div>
      <div className="mt-1.5 text-[11px] text-slate-500 tabular-nums">
        {money.fx_complete
          ? `${fmtGel(String(Math.round(net)))} after ${fmtGel(
              String(Math.round(money.expenses_gel)),
            )} costs`
          : "Some deals have no FX rate — total understates"}
      </div>
    </Panel>
  );
}

export function ReportsPanel({ people }: { people: PersonRef[] }) {
  if (people.length === 0) return null;
  return (
    <Panel title={`Your reports · ${people.length}`} flush>
      <ScrollList max={240} deps={people.length}>
        {people.map((p) => (
          <Link
            key={p.id}
            to={`/team/${p.id}`}
            className="flex items-center gap-2.5 px-4 py-1.5 hover:bg-white/[0.02] transition"
          >
            <Avatar name={p.full_name || p.email} size="xs" status={p.presence} />
            <span className="text-[12.5px] text-slate-200 truncate flex-1 min-w-0">
              {p.full_name || p.email}
            </span>
            <span className="text-[10.5px] text-slate-500 flex-shrink-0">
              {roleLabel(p.role)}
            </span>
            <span className="text-[10.5px] text-slate-600 flex-shrink-0 w-[52px] text-right tabular-nums">
              {p.presence === "active" ? "online" : relativeAgo(p.last_active_at) ?? ""}
            </span>
          </Link>
        ))}
      </ScrollList>
    </Panel>
  );
}

/** Who is around right now — the headline counts, then the actual people.
 *
 *  This replaced the app-wide `TeamPresenceCard`, which hand-rolled its own
 *  card styling and row scale so it never lined up with anything on this page.
 *  Rows arrive from the server already ordered active → away → offline, so the
 *  people worth looking at sit at the top of the scroll area and the panel
 *  stays a fixed height regardless of headcount.
 */
/** Presence rows are a fixed two-line block; measured at 41px in the live DOM.
 *  Ten of them is the ask, and the panel scrolls beyond that — the agency has
 *  13 people in the list today. */
const PRESENCE_ROW_H = 41;
const PRESENCE_ROWS = 10;

export function PresencePanel({
  p,
  to,
}: {
  p: PresenceSummary;
  to?: string;
}) {
  return (
    <Panel
      title="Team presence"
      right={
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            <span className="text-slate-300">{p.active}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
            <span className="text-slate-300">{p.away}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
            <span className="text-slate-300">{p.offline}</span>
          </span>
          {to && (
            <Link to={to} className="text-accent-300 hover:underline ml-1">
              open
            </Link>
          )}
        </span>
      }
      flush
    >
      {p.people.length === 0 ? (
        <Quiet title="Nobody here" />
      ) : (
        // Ten rows visible, the rest reachable by scrolling inside the panel.
        // This is the single tallest thing on a leadership Home by a wide
        // margin — see the note on the one-screen budget in the Home plan.
        <ScrollList max={PRESENCE_ROWS * PRESENCE_ROW_H} deps={p.people.length}>
          {p.people.map((person) => {
            // Reuses the app's shared presence vocabulary so Home, Audit and the
            // Employee page all describe the same state with the same words.
            const label = presenceLabel(
              person.presence,
              person.last_active_at,
              person.last_seen_at,
            );
            return (
              <Link
                key={person.id}
                to={`/team/${person.id}`}
                className="flex items-center gap-2.5 px-4 py-1.5 hover:bg-white/[0.02] transition min-w-0"
              >
                <Avatar
                  name={person.full_name || person.email}
                  size="xs"
                  status={person.presence}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] text-slate-200 truncate leading-tight">
                    {person.full_name || person.email}
                  </span>
                  <span
                    className={`block text-[10.5px] truncate leading-tight ${
                      label?.tone === "active"
                        ? "text-emerald-300"
                        : label?.tone === "away"
                          ? "text-amber-300"
                          : "text-slate-500"
                    }`}
                  >
                    {label?.text ?? "Never signed in"}
                  </span>
                </span>
              </Link>
            );
          })}
        </ScrollList>
      )}
    </Panel>
  );
}

/** Two people listed as managing each other. Surfaced rather than hidden, so
 *  someone fixes the org chart instead of the code papering over it. */
export function MutualLinkNotice({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <div className="text-[11px] text-slate-500 px-1 pt-1.5">
      You and {names.join(", ")} each manage the other —{" "}
      <Link to="/team" className="text-accent-300 hover:underline">
        fix in Team
      </Link>
    </div>
  );
}
